import { useEffect, useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { TaskList } from './TaskList'
import type { ReactNode } from 'react'
import type { TaskViewContext } from './TaskPageLayout'
import type { TaskReorderEvent } from './TaskList'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

export type ListDetailsViewSection = {
  id: string
  title: string
  /** Optional leading icon in the header (e.g. the status ring). */
  icon?: ReactNode
  items: Array<TaskDocument>
  /** Show only the first N rows, then "Show N more" (design 13a). */
  collapseAfter?: number
  /** Extra control in the header, e.g. "Reschedule all". */
  headerAction?: ReactNode
  /** Hovering the header shows a + that adds a task into this group (design 18b). */
  onAdd?: () => void
  collapsible?: boolean
  defaultOpen?: boolean
  hideIfEmpty?: boolean
  showCount?: boolean
  onReorder?: (event: TaskReorderEvent) => void
  className?: string
  headerClassName?: string
  listClassName?: string
}

type ListDetailsViewProps = TaskViewContext & {
  sections?: Array<ListDetailsViewSection>
}

export function ListDetailsView({
  items,
  isLoading,
  defaultOnReorder,
  onSelect,
  selectedTaskId,
  containerClassName,
  containerStyle,
  itemClassName,
  formatDueDate,
  viewMode,
  sections = [],
}: ListDetailsViewProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const { t } = useTranslation('tasks')

  const visibleItems = (section: ListDetailsViewSection) =>
    section.collapseAfter && !expandedSections[section.id]
      ? section.items.slice(0, section.collapseAfter)
      : section.items

  const renderShowMore = (section: ListDetailsViewSection) => {
    const hidden = section.collapseAfter ? section.items.length - section.collapseAfter : 0
    if (hidden <= 0) return null
    const expanded = !!expandedSections[section.id]
    return (
      <button
        type="button"
        onClick={() => setExpandedSections((prev) => ({ ...prev, [section.id]: !expanded }))}
        className="flex w-full items-center gap-1.5 border-t px-3 py-2 text-[13px] text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        <ChevronDown className={cn('size-3.5 transition-transform', expanded && 'rotate-180')} />
        {expanded ? t('grouping.showLess') : t('grouping.showMore', { count: hidden })}
      </button>
    )
  }

  useEffect(() => {
    if (sections.length === 0) {
      setOpenSections({})
      return
    }

    setOpenSections((previous) => {
      const next: Record<string, boolean> = {}
      let changed = false

      sections.forEach((section) => {
        if (!section.collapsible) {
          return
        }

        const existing = previous[section.id]
        if (typeof existing === 'boolean') {
          next[section.id] = existing
          return
        }

        next[section.id] = section.defaultOpen ?? true
        changed = true
      })

      if (Object.keys(previous).length !== Object.keys(next).length) {
        changed = true
      }

      if (!changed) {
        return previous
      }

      return next
    })
  }, [sections])

  if (isLoading) {
    return (
      <TaskList
        items={items}
        className={cn(containerClassName, 'min-w-0 w-full')}
        style={containerStyle}
        itemClassName={itemClassName}
        formatDueDate={formatDueDate}
        isLoading
        virtualized={false}
        viewMode={viewMode}
      />
    )
  }

  if (sections.length === 0) {
    return (
      <TaskList
        items={items}
        className={cn(containerClassName, 'min-w-0 w-full')}
        style={containerStyle}
        itemClassName={itemClassName}
        formatDueDate={formatDueDate}
        onReorder={defaultOnReorder}
        onSelect={onSelect}
        selectedTaskId={selectedTaskId}
        virtualized={false}
        viewMode={viewMode}
      />
    )
  }

  const firstVisibleSectionId = sections.find(
    (section) => !(section.hideIfEmpty && section.items.length === 0),
  )?.id

  // Only show empty state if ALL sections are empty
  const allSectionsEmpty = sections.every(
    (section) => section.items.length === 0,
  )

  return (
    // Sections stack vertically, so this wrapper is the scroll container for
    // the whole column — the per-section lists size to their content.
    <div
      className={cn(
        containerClassName,
        // Phone FAB overlays the scrollport bottom-right; pad so last rows clear it (MOL-37).
        'min-w-0 w-full space-y-4 overflow-y-auto phone:pb-20',
      )}
      style={containerStyle}
    >
      {sections.map((section) => {
        if (section.hideIfEmpty && section.items.length === 0) {
          return null
        }

        const isFirstVisible = section.id === firstVisibleSectionId
        const sectionReorderHandler = section.onReorder ?? defaultOnReorder
        const listClassName = cn('min-w-0', section.listClassName)

        if (section.collapsible) {
          const isOpen =
            typeof openSections[section.id] === 'boolean'
              ? openSections[section.id]
              : (section.defaultOpen ?? true)

          return (
            <Collapsible
              key={section.id}
              open={isOpen}
              onOpenChange={(nextOpen) =>
                setOpenSections((previous) => ({
                  ...previous,
                  [section.id]: nextOpen,
                }))
              }
              className={cn(
                'space-y-2',
                section.className,
                isFirstVisible && 'pt-4',
              )}
            >
              <div
                className={cn(
                  'group/section flex items-center justify-start gap-2 px-2 pl-6 pr-2',
                  section.headerClassName,
                )}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] font-semibold text-foreground transition hover:bg-muted"
                  >
                    <ChevronDown
                      className={cn(
                        'size-3.5 text-muted-foreground transition-transform',
                        !isOpen && '-rotate-90',
                      )}
                    />
                    {section.icon}
                    <span>{section.title}</span>
                    {section.showCount !== false && (
                      <span className="text-xs font-medium tabular-nums text-muted-foreground">
                        {section.items.length}
                      </span>
                    )}
                  </button>
                </CollapsibleTrigger>
                {section.headerAction}
                {section.onAdd && (
                  <button
                    type="button"
                    onClick={section.onAdd}
                    aria-label={t('quickAdd.addTo', { group: section.title })}
                    title={t('quickAdd.addTo', { group: section.title })}
                    className="ml-auto flex size-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/section:opacity-100"
                  >
                    <Plus className="size-3.5" />
                  </button>
                )}
              </div>
              <CollapsibleContent>
                <TaskList
                  items={visibleItems(section)}
                  className={listClassName}
                  itemClassName={itemClassName}
                  formatDueDate={formatDueDate}
                  onReorder={sectionReorderHandler}
                  onSelect={onSelect}
                  selectedTaskId={selectedTaskId}
                  virtualized={false}
                  viewMode={viewMode}
                  showEmptyState={allSectionsEmpty}
                  footer={renderShowMore(section)}
                />
              </CollapsibleContent>
            </Collapsible>
          )
        }

        return (
          <div
            key={section.id}
            className={cn(section.className, isFirstVisible && 'pt-4')}
          >
            <div
              className={cn(
                'px-2 pl-8 text-sm font-semibold text-foreground',
                section.headerClassName,
              )}
            >
              {section.title}
              {section.showCount ? (
                <span className="ml-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {section.items.length}
                </span>
              ) : null}
            </div>
            <TaskList
              items={section.items}
              className={listClassName}
              itemClassName={itemClassName}
              formatDueDate={formatDueDate}
              onReorder={sectionReorderHandler}
              onSelect={onSelect}
              selectedTaskId={selectedTaskId}
              virtualized={false}
              viewMode={viewMode}
              showEmptyState={allSectionsEmpty}
            />
          </div>
        )
      })}
    </div>
  )
}

export default ListDetailsView
